import React, { useMemo } from 'react';

import { Avatar } from '@change/ds-core-ui/Avatar';
import { CardHeader } from '@change/ds-core-ui/Card';
import { Spinner } from '@change/ds-core-ui/Spinner';
import { Text } from '@change/ds-core-ui/Text';
import { EndorserBorderIcon } from '@change/ds-icons/EndorserBorderIcon';
import { cn } from '@change/ds-utils';

import { useProcessedImageUrl } from '#src/app/shared/hooks/image';

import { isFieldFocused, shouldHighlightText } from '../../helpers/validation';
import type { FocusedField } from '../../types';
import { ActionsMenu } from '../ActionsMenu';
import { LinkContent } from '../LinkContent';

type EndorsementCardHeaderProps = {
	displayName: string;
	displayTitle: string;
	photoUrl: string;
	link?: string;
	isNamePlaceholder: boolean;
	isTitlePlaceholder: boolean;
	focusedField: FocusedField;
	onEdit?: () => void;
	onRemove?: () => void;
};

export const EndorsementCardHeader: React.FC<EndorsementCardHeaderProps> = ({
	displayName,
	displayTitle,
	photoUrl,
	link,
	isNamePlaceholder,
	isTitlePlaceholder,
	focusedField,
	onEdit,
	onRemove,
}) => {
	const image = useMemo(() => {
		if (!photoUrl) return undefined;
		return { url: photoUrl, processing: true };
	}, [photoUrl]);

	const resolvedPhotoUrl = useProcessedImageUrl(image);
	const isImageProcessing = !!image && !resolvedPhotoUrl;

	const isNameFocused = isFieldFocused('name', focusedField) || isFieldFocused('url', focusedField);
	const isTitleFocused = isFieldFocused('title', focusedField);
	const shouldHighlightTitle = shouldHighlightText(isTitleFocused, !isTitlePlaceholder);

	const avatarElement = isImageProcessing ? (
		<div
			className="bg-background-raised flex size-14 shrink-0 items-center justify-center rounded-full"
			data-testid="endorsement-photo-processing"
			aria-hidden="true"
		>
			<Spinner className="size-4" />
		</div>
	) : (
		<div className="relative flex h-[68px] w-[68px] shrink-0 items-center justify-center" data-testid="endorser-badge">
			<EndorserBorderIcon size={68} className={resolvedPhotoUrl ? 'text-icon-information' : 'text-icon-weak'} />
			<Avatar
				// eslint-disable-next-line @change-ds/ds-no-raw-color-classname -- white gap between photo and endorser badge border in both modes (no fixed stroke token)
				className="absolute z-0 min-h-[52px] min-w-[52px] border-2 border-white"
				size={52}
				alt={displayName}
				src={resolvedPhotoUrl || undefined}
			/>
		</div>
	);

	return (
		<CardHeader className="flex min-w-0 items-center justify-between overflow-hidden p-0!">
			<div className="flex min-w-0 flex-1 items-center gap-4">
				{avatarElement}
				<div className="flex min-w-0 flex-col justify-between break-words">
					<LinkContent name={displayName} link={link} isPlaceholder={isNamePlaceholder} hasFocus={isNameFocused} />
					<Text
						variant="body"
						size="small"
						fontWeight="normal"
						className={cn('text-typography-strong break-words', shouldHighlightTitle && 'text-typography-interactive!')}
					>
						{displayTitle}
					</Text>
				</div>
			</div>
			{(onEdit || onRemove) && <ActionsMenu onEdit={onEdit} onRemove={onRemove} />}
		</CardHeader>
	);
};
